import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { createChatRoom, CHAT_ROOM_TYPES } from '../../lib/api/chatApi';
import client from '../../lib/api/client';

/**
 * 채널 생성 다이얼로그
 */
const CreateChannelDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [chatRoomTyp, setChatRoomTyp] = useState('NORMAL');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [allUsers, setAllUsers] = useState([]); // 전체 사원 목록 캐시
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // 다이얼로그 열릴 때 전체 사원 목록 미리 로드
  useEffect(() => {
    if (!open) return;
    const loadAllUsers = async () => {
      try {
        setUserSearchLoading(true);
        const response = await client.get('/api/v1/user/list', {
          params: { useYn: 'Y' }
        });
        const list = response.data.list || [];
        setAllUsers(list);
        setUserOptions(list);
      } catch (error) {
        console.error('사원 목록 로드 실패:', error);
      } finally {
        setUserSearchLoading(false);
      }
    };
    loadAllUsers();
  }, [open]);

  // 검색어 입력 시 클라이언트 사이드 필터링
  useEffect(() => {
    if (!userSearchKeyword.trim()) {
      setUserOptions(allUsers);
      return;
    }
    const lk = userSearchKeyword.toLowerCase();
    setUserOptions(
      allUsers.filter(u =>
        (u.usrNm && u.usrNm.toLowerCase().includes(lk)) ||
        (u.emlAddr && u.emlAddr.toLowerCase().includes(lk))
      )
    );
  }, [userSearchKeyword, allUsers]);

  // 다이얼로그 초기화
  useEffect(() => {
    if (open) {
      setName('');
      setChatRoomTyp('NORMAL');
      setSelectedUsers([]);
      setError('');
    }
  }, [open]);

  // 채널 생성
  const handleCreate = async () => {
    if (!name.trim()) {
      setError('채널 이름을 입력해주세요.');
      return;
    }

    try {
      setCreating(true);
      setError('');

      const chatRoom = {
        name: name.trim(),
        chatRoomTyp,
        participants: selectedUsers.map(user => ({
          usrUuid: user.usrUuid,
          ruleTyp: 'MEMBER'
        }))
      };

      const created = await createChatRoom(chatRoom);
      onCreated?.(created);
      onClose();
    } catch (error) {
      console.error('채널 생성 실패:', error);
      setError('채널 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  // 사용자 제거
  const handleRemoveUser = (usrUuid) => {
    setSelectedUsers(prev => prev.filter(u => u.usrUuid !== usrUuid));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" />
            <Typography variant="h6">새 채널 만들기</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
          {/* 채널 이름 */}
          <TextField
            label="채널 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            placeholder="예: 프로젝트 A 팀"
            error={!!error && !name.trim()}
            helperText={error && !name.trim() ? error : ''}
          />

          {/* 채널 유형 */}
          <FormControl fullWidth>
            <InputLabel>채널 유형</InputLabel>
            <Select
              value={chatRoomTyp}
              onChange={(e) => setChatRoomTyp(e.target.value)}
              label="채널 유형"
            >
              <MenuItem value="NORMAL">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  💬 일반 채널
                </Box>
              </MenuItem>
              <MenuItem value="PROJECT">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📁 프로젝트 채널
                </Box>
              </MenuItem>
              <MenuItem value="TEAM">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  👥 팀 채널
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* 참여자 초대 */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              <PersonAddIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              참여자 초대 (선택)
            </Typography>
            <Autocomplete
              multiple
              options={userOptions}
              getOptionLabel={(option) => option.usrNm || option.emlAddr || ''}
              value={selectedUsers}
              onChange={(e, newValue) => setSelectedUsers(newValue)}
              loading={userSearchLoading}
              onInputChange={(e, value) => setUserSearchKeyword(value)}
              isOptionEqualToValue={(option, value) => option.usrUuid === value.usrUuid}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="이름 또는 이메일로 검색..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearchLoading && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {option.usrNm?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.usrNm}</Typography>
                      <Typography variant="caption" color="text.secondary">
                      {option.emlAddr}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
              renderTags={() => null} // 아래 리스트로 표시
            />

            {/* 선택된 사용자 목록 */}
            {selectedUsers.length > 0 && (
              <List dense sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                {selectedUsers.map((user) => (
                  <ListItem
                    key={user.usrUuid}
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => handleRemoveUser(user.usrUuid)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                        {user.usrNm?.charAt(0) || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.usrNm}
                      secondary={user.emlAddr}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* 에러 메시지 */}
          {error && name.trim() && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          startIcon={creating ? <CircularProgress size={16} /> : null}
        >
          {creating ? '생성 중...' : '채널 만들기'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChannelDialog;
